import { Dispatch } from 'react'
import { ActiveSpeaker, Participant, VideoPlayer } from '@zoom/videosdk'
import { ClassroomState, UserState } from 'api/classroom/ClassroomState'
import { Coords, RtcSession, ConferenceAction, ConferenceParticipant } from 'contexts/conference/types'
import { ZoomSDKClient, ZoomSDKStream, ZoomClientOptions } from './types'
import { PrimusClient } from 'primus'
import { ChatClient } from 'chat'
import { WseUserId, ParticipantHardMuteStatus } from 'api/types'
import { logLocalAudioApplied, logLocalAudioError } from 'services/breakoutFaroLogger'
import { startZoomQualityMonitor, stopZoomQualityMonitor } from 'services/zoomDiagnostics/zoomQualityMonitor'
import {
    logZoomNetworkError,
    logZoomSessionClosed,
    logZoomUserAdded,
    logZoomUserRemoved,
    logZoomParticipantDisconnect
} from 'services/zoomLifecycleFaroLogger'
import {
    classifyLocalConnectionRemovalCause,
    classifyRemoteUserRemovedCause
} from 'services/zoomDiagnostics/removalCause'
import {
    consumeLocalExpelPending,
    isLocalExpelPending
} from 'services/zoomDiagnostics/zoomDiagnosticsSessionContext'

class ZoomSession implements RtcSession {
    private readonly userDetails = new Map<string, UserState>()
    private readonly videoElements = new Map<number, VideoPlayer>()
    private readonly currentUser: Participant

    async muteLocally (participant: ConferenceParticipant): Promise<void> {
        try {
            await this.stream.muteUserAudioLocally(+participant.clientUserId)
            logLocalAudioApplied({
                action: 'mute',
                targetUserId: participant.wseUserId,
                clientUserId: participant.clientUserId
            })
        } catch (error) {
            logLocalAudioError({
                action: 'mute',
                targetUserId: participant.wseUserId,
                clientUserId: participant.clientUserId,
                reason: error instanceof Error ? error.message : String(error)
            })
            throw error
        }
    }

    async unmuteLocally (participant: ConferenceParticipant): Promise<void> {
        try {
            await this.stream.unmuteUserAudioLocally(+participant.clientUserId)
            logLocalAudioApplied({
                action: 'unmute',
                targetUserId: participant.wseUserId,
                clientUserId: participant.clientUserId
            })
        } catch (error) {
            logLocalAudioError({
                action: 'unmute',
                targetUserId: participant.wseUserId,
                clientUserId: participant.clientUserId,
                reason: error instanceof Error ? error.message : String(error)
            })
            throw error
        }
    }

    isUserAudioMutedLocally (clientUserId: string): boolean {
        return this.stream.isUserAudioMutedLocally(+clientUserId)
    }

    async stopRenderVideo (participant: ConferenceParticipant): Promise<void> {
        const userId = +participant.clientUserId
        if (this.videoElements.has(userId)) {
            await this.stream.detachVideo(userId)
            const videoElement = this.videoElements.get(userId)

            if (videoElement?.parentElement) {
                videoElement.parentElement.removeChild(videoElement)
            }
            this.videoElements.delete(userId)
        }
    }

    async pauseRenderVideo (participant: ConferenceParticipant): Promise<void> {
        const userId = +participant.clientUserId
        const videoElement = this.videoElements.get(userId)
        if (videoElement) {
            await this.stream.detachVideo(userId, videoElement)
        }
    }

    async excludeLocally (participant: ConferenceParticipant): Promise<void> {
        await this.stopRenderVideo(participant)
        await this.muteLocally(participant)
    }

    async adjustUserAudioVolumeLocally (userId: number, volume: number): Promise<void> {
        await this.stream.adjustUserAudioVolumeLocally(userId, volume)
    }

    async setVideoMuted (muted: boolean): Promise<void> {
        if (muted) {
            const vbHelper = this.stream.getVirtualbackgroundStatus()
            if (vbHelper?.imageSrc) {
                await this.stream.updateVirtualBackgroundImage('')
            }
            await this.stream.stopVideo()
        } else {
            const videoOptions = this.stream.isRenderSelfViewWithVideoElement()
                ? { videoElement: document.querySelector('#self-video-element') as HTMLVideoElement }
                : this.options.video
            await this.stream.startVideo(videoOptions)
            const isBgApplied = sessionStorage.getItem('toggleVirtualBackground') === 'true'
            const isTeacher = sessionStorage.getItem('role') === 'teacher'
            const background = isBgApplied ? sessionStorage.getItem(isTeacher ? 'teacherBackground' : 'studentSelectedBackground') || '' : ''
            void this.setVideoBackground(background)
        }

        this.dispatch({ method: 'mute-video', muted })
    }

    async setAudioMuted (muted: boolean, isFromHardMute: boolean = false): Promise<void> {
        try {
            if (isFromHardMute) {
                if (muted) {
                    await this.stream.muteAudio()
                }
                this.dispatch({ method: 'hard-mute-audio', muted })
            } else {
                if (!muted) {
                    await this.stream.unmuteAudio()
                } else {
                    await this.stream.muteAudio()
                }
                this.dispatch({ method: 'mute-audio', muted })
            }
        } catch (error: any) {
            // Error 6015 (INVALID_OPERATION) or 5002 (IMPROPER_MEETING_STATE) occurs when trying to modify audio state
            // while actively transitioning between subsessions (no audio joined yet, or main session closed temporarily).
            if (error?.errorCode === 6015 || error?.errorCode === 5002) {
                console.warn(`[ZoomSession] Suppressing ${error.errorCode} error during subsession transition`, error)
                // We still want the UI to reflect the requested muted state even if Zoom SDK rejected the physical call
                if (isFromHardMute) {
                    this.dispatch({ method: 'hard-mute-audio', muted })
                } else {
                    this.dispatch({ method: 'mute-audio', muted })
                }
            } else {
                console.error('[ZoomSession] Error setting audio muted state:', error)
                throw error
            }
        }
    }

    async setVideoFrame (participant: ConferenceParticipant, { top, left, width, height }: Coords): Promise<void> {
        const userId = +participant.clientUserId

        if (participant.cameraStatus === 'on') {
            if (!this.videoElements.has(userId)) {
                const videoElement = document.createElement('video-player') as VideoPlayer
                videoElement.id = `video-${userId}`

                videoElement.style.position = 'absolute'
                videoElement.style.left = `${left}px`
                videoElement.style.top = `${top}px`
                videoElement.style.width = `${width}px`
                videoElement.style.height = `${height}px`

                const container = document.getElementById('video-player-container')
                if (container) {
                    container.appendChild(videoElement)
                    this.videoElements.set(userId, videoElement)
                    await this.stream.attachVideo(userId, this.options.videoQuality, videoElement)
                }
            } else {
                const videoElement = this.videoElements.get(userId)
                if (videoElement) {
                    videoElement.style.left = `${left}px`
                    videoElement.style.top = `${top}px`
                    videoElement.style.width = `${width}px`
                    videoElement.style.height = `${height}px`

                    await this.stream.attachVideo(userId, this.options.videoQuality, videoElement)
                }
            }
        } else {
            // Camera is OFF, but if video element exists, update its position
            if (this.videoElements.has(userId)) {
                const videoElement = this.videoElements.get(userId)
                if (videoElement) {
                    videoElement.style.left = `${left}px`
                    videoElement.style.top = `${top}px`
                    videoElement.style.width = `${width}px`
                    videoElement.style.height = `${height}px`
                }
            }
        }
    }

    async leave (): Promise<void> {
        stopZoomQualityMonitor()
        this.dispatch({ method: 'disconnected' })
        await this.client.leave(false)
    }

    async end (): Promise<void> {
        stopZoomQualityMonitor()
        this.dispatch({ method: 'disconnected' })
        await this.stopCloudRecording()
        await this.client.leave(true)
    }

    async stopCloudRecording (): Promise<void> {
        const cloudRecording = this.client.getRecordingClient()
        await cloudRecording.stopCloudRecording()
    }

    private setLastActiveSpeaker (activeSpeakers: ActiveSpeaker[]): void {
        const speakers = activeSpeakers.map(speaker => this.userDetails.get(JSON.parse(speaker.displayName ?? '{}').id))
        const isTeacher = speakers.find(speaker => speaker?.role === 'teacher')
        if (isTeacher) {
            this.dispatch({ method: 'last-speaker', speakerId: isTeacher.id })

        } else {
            this.dispatch({ method: 'last-speaker', speakerId: speakers[0]?.id })
        }
    }

    async updateParticipantsHardMuteStatus (participantHardMuteStatus: ParticipantHardMuteStatus[], id: string, hardMuteStatus: boolean): Promise<void> {
        const updatedParticipantHardMuteStatus = participantHardMuteStatus.map(
            student => student.id === id ? { ...student, micHardMuted: hardMuteStatus } : student)
        this.dispatch({
            participantHardMuteStatus: updatedParticipantHardMuteStatus,
            method: 'update-hard-mute-status'
        })
    }

    async studentRemoveStatus (): Promise<void> {
        await this.leave()
    }

    async setVideoBackground (data: string): Promise<void> {
        await this.stream.updateVirtualBackgroundImage(data)
        this.dispatch({ method: 'update-video-bg', data })
    }

    getSubsessionClient (): any {
        try {
            // Return the actual subsession client from Zoom SDK
            return this.client.getSubsessionClient?.()
        } catch (error) {
            console.warn('[ZoomSession] Subsession client error:', error)
            return null
        }
    }

    getZoomSDKClient (): ZoomSDKClient {
        return this.client
    }

    getLiveParticipants (): ConferenceParticipant[] {
        const allUsers: ConferenceParticipant[] = []
        const seenUserIds = new Set<number>()

        // 1. Get users from current context (same room/main session)
        const currentUsers = this.client.getAllUser()
        for (const u of currentUsers) {
            if (!seenUserIds.has(u.userId)) {
                seenUserIds.add(u.userId)
                allUsers.push(this.getGOCParticipant(u))
            }
        }

        return allUsers
    }

    public constructor (
        primus: PrimusClient,
        chatClient: ChatClient,
        private readonly wseUserId: WseUserId,
        private readonly client: ZoomSDKClient,
        classroomState: ClassroomState,
        private readonly stream: ZoomSDKStream,
        private readonly dispatch: Dispatch<ConferenceAction>,
        private readonly options: ZoomClientOptions
    ) {
        this.userDetails.set(classroomState.teacher.id, classroomState.dcAppData.teacher)
        for (const student of classroomState.dcAppData.students) {
            this.userDetails.set(student.id, student)
        }
        this.updateparticipantHardMuteStatus(classroomState.dcAppData.students)

        this.updateParticipantsList()

        client.on('connection-change', payload => {
            const { state, reason } = payload as { state?: string; reason?: string }
            if (state === 'Reconnecting' || state === 'Fail') {
                logZoomNetworkError({
                    connectionState: state,
                    reason
                })
            } else if (state === 'Closed') {
                logZoomSessionClosed(state, reason)
            }

            if (state === 'Closed' || state === 'Fail') {
                const localExpelPending = consumeLocalExpelPending()
                const removalCause = classifyLocalConnectionRemovalCause(state, reason, { localExpelPending })
                logZoomParticipantDisconnect({
                    removalCause,
                    rawReason: reason,
                    connectionState: state
                })
            }
        })

        client.on('user-added', participants => {
            this.updateParticipantsList()
            for (const participant of participants) {
                logZoomUserAdded(String(participant.userId), participant.displayName)
            }
        })

        client.on('user-removed', participants => {
            for (const participant of participants) {
                const userId = participant.userId
                if (this.videoElements.has(userId)) {
                    void this.stream.detachVideo(userId)
                    const videoElement = this.videoElements.get(userId)
                    if (videoElement?.parentElement) {
                        videoElement.parentElement.removeChild(videoElement)
                    }
                    this.videoElements.delete(userId)
                }
            }

            void this.updateVideoRendering()
            this.updateParticipantsList()
            for (const participant of participants) {
                const gocUser = this.parseGOCUserInfoFromDisplayName(participant.displayName)
                const removalCause = classifyRemoteUserRemovedCause({
                    localExpelPending: isLocalExpelPending(),
                    participantWseUserId: gocUser?.id,
                    currentWseUserId: wseUserId
                })
                logZoomUserRemoved(String(participant.userId), participant.displayName, removalCause)
            }
        })

        client.on('user-updated', participants => {
            void this.updateVideoRendering()
            this.updateParticipantsList()
        })

        let speakerTimeout: NodeJS.Timeout
        client.on('active-speaker', activeSpeakers => {
            dispatch({ method: 'speakers-list', list: new Set(activeSpeakers.map(speaker => speaker.userId.toString())) })
            this.setLastActiveSpeaker(activeSpeakers)

            clearTimeout(speakerTimeout)
            speakerTimeout = setTimeout(() => {
                dispatch({ method: 'speakers-list', list: new Set() })
            }, 2000)
        })

        const wseUser = this.userDetails.get(wseUserId)
        this.currentUser = client.getCurrentUserInfo()

        if (!this.options.isAudioMuted) {
            void this.setAudioMuted(this.options.isAudioMuted)
        }

        if (!this.options.isVideoMuted && !this.stream.isRenderSelfViewWithVideoElement()) {
            void this.setVideoMuted(this.options.isVideoMuted)
        }

        void this.stream.enableBackgroundNoiseSuppression(true)

        const cloudRecording = this.client.getRecordingClient()
        const canStartRecording = cloudRecording.canStartRecording()
        const isGocTeacher = document.cookie.split('; ').find(row => row.startsWith('isGOCTeacher='))?.split('=')[1] === 'true'
        if (canStartRecording && wseUser?.role === 'teacher' && isGocTeacher) {
            void cloudRecording.startCloudRecording()
        }

        this.dispatch({
            method: 'session-joined',
            rtcSession: this,
            classroomState,
            primus,
            chatClient,
            isAudioMuted: options.isAudioMuted ?? true,
            isVideoMuted: options.isVideoMuted ?? true,
            allowVideo: !(options.isVideoDisabled ?? wseUser?.camHardMuted ?? true),
            allowAudio: !(wseUser?.micHardMuted ?? true),
            isRenderSelfViewWithVideoElement: this.stream.isRenderSelfViewWithVideoElement()
        })

        const sessionTimeLeftSeconds = classroomState.dcAppData.session.timeLeft
        let midSessionDelayMs: number | undefined
        if (sessionTimeLeftSeconds > 0) {
            midSessionDelayMs = Math.floor((sessionTimeLeftSeconds / 2) * 1000)
        }

        void startZoomQualityMonitor(client, stream, { midSessionDelayMs })
    }

    private parseGOCUserInfoFromDisplayName (displayName?: string): UserState | undefined {
        try {
            if (!displayName) {
                return void 0
            }

            return this.userDetails.get((JSON.parse(displayName) as { id: string }).id)

        } catch (e) {
            return void 0
        }
    }

    private parseGOCUserInfo (participant: Participant): UserState | undefined {
        return this.parseGOCUserInfoFromDisplayName(participant.displayName)
    }

    private getGOCParticipant (zoomUser: Participant): ConferenceParticipant {
        const wseUser = this.parseGOCUserInfo(zoomUser)

        if (wseUser) {
            return {
                clientUserId: zoomUser.userId.toString(),
                cameraStatus: zoomUser.bVideoOn ? 'on' : 'off',
                audioStatus: zoomUser.audio ? 'on' : 'off',
                audioMuted: zoomUser.muted ?? false,
                lastName: wseUser.lastName,
                firstName: wseUser.firstName,
                wseProfilePhotoUrl: wseUser.photoUri,
                wseUserId: wseUser.id,
                role: wseUser.role,
                micHardMuted: wseUser.micHardMuted,
                expelled: wseUser.expelled
            }

        } else {
            console.warn(`A non-goc client (${zoomUser.userId}) found in the zoom session.`)

            return {
                clientUserId: zoomUser.userId.toString(),
                cameraStatus: zoomUser.bVideoOn ? 'on' : 'off',
                audioStatus: zoomUser.audio ? 'on' : 'off',
                audioMuted: zoomUser.muted ?? false,
                lastName: zoomUser.userId.toString(),
                firstName: zoomUser.displayName ?? 'Unknown',
                role: 'student',
                wseUserId: '',
                micHardMuted: false,
                expelled: false
            }
        }
    }

    private updateParticipantsList (): void {
        this.dispatch({
            participants: this.client.getAllUser().map(zoomUser => this.getGOCParticipant(zoomUser)),
            method: 'set-participants'
        })
    }

    private updateparticipantHardMuteStatus (students: UserState[]): void {
        this.dispatch({
            participantHardMuteStatus: students.map(student => {
                return { id: student.id, micHardMuted: student.micHardMuted }
            }),
            method: 'update-hard-mute-status'
        })
    }

    private async updateVideoRendering (): Promise<void> {
        for (const zoomUser of this.client.getAllUser().filter(zoomUser => zoomUser.userId !== this.currentUser.userId)) {
            if (this.videoElements.has(zoomUser.userId) && !zoomUser.bVideoOn) {
                const conferenceParticipant = this.getGOCParticipant(zoomUser)
                await this.pauseRenderVideo(conferenceParticipant)
            }
        }
    }
}

export { ZoomSession }
